import { useQuery } from "@apollo/client";
import { Alert } from "@mui/material";
import { StatCard } from "components/StatCard";
import { WasteScheduleAdherenceSummaryQuery } from "generated-graphql/graphql";
import { StatCardGrid } from "routes/Customer/Waste/components/StatCardGrid";
import { InvoiceTrackingListHeading } from "routes/Customer/Waste/Invoices/InvoiceTrackingListHeading";
import {
  InvoiceTrackingSummaryCard,
  InvoiceTrackingSummarySkeleton,
} from "routes/Customer/Waste/Invoices/InvoiceTrackingSummaryCard";

import { WASTE_SCHEDULE_ADHERENCE_SUMMARY_QUERY } from "./queries";

type Summary = WasteScheduleAdherenceSummaryQuery["wasteScheduleAdherence"]["summary"];

function formatPercent(value: number | null | undefined): string {
  return value == null ? "—" : `${Math.round(value * 100)}%`;
}

/**
 * The period's totals, on the waste dashboards' own stat cards.
 *
 * A figure is coloured only where it is a finding against the vendor: service
 * the schedule did not plan. A window nothing evidences may be a missed pickup
 * or a month nobody has imported, and colouring it would claim the one
 * conclusion the audit refuses to draw.
 */
export function SummaryTiles({ summary }: { summary: Summary }) {
  const finding = (count: number) => (count > 0 ? "warning.dark" : "text.primary");
  return (
    <StatCardGrid testId="schedule-adherence-totals">
      <StatCard
        captionPlacement="top"
        caption="Within schedule"
        value={formatPercent(summary.withinSchedulePercent)}
        subCaption={`${summary.windowsServed} of ${summary.scheduledWindows} windows`}
      />
      <StatCard
        captionPlacement="top"
        caption="Outside schedule"
        value={String(summary.visitsOutsideWindow)}
        subCaption="Visits"
        valueColor={finding(summary.visitsOutsideWindow)}
      />
      <StatCard
        captionPlacement="top"
        caption="No service shown"
        value={String(summary.windowsWithNoService)}
        subCaption="Windows"
      />
      <StatCard
        captionPlacement="top"
        caption="Unscheduled"
        value={String(summary.unscheduledVisits)}
        subCaption="Visits"
        valueColor={finding(summary.unscheduledVisits)}
      />
      <StatCard
        captionPlacement="top"
        caption="Can't compare"
        value={String(summary.rowsNeedingReview)}
        subCaption="Results"
      />
    </StatCardGrid>
  );
}

/**
 * Every window unresolved, nothing served, nothing billed outside a window: the
 * shape of a period whose invoices have not been imported. The audit reports it
 * per window, correctly, but a reader needs to be told once.
 */
function noInvoiceCoverage(summary: Summary): boolean {
  return (
    summary.scheduledWindows > 0 &&
    summary.windowsServed === 0 &&
    summary.visitsOutsideWindow === 0 &&
    summary.unscheduledVisits === 0 &&
    summary.rowsNeedingReview === summary.scheduledWindows
  );
}

/**
 * The totals for the whole period, asked for on the vendor and period alone so
 * that filtering the rows below cannot move them.
 */
export function PeriodTotals({
  tenantId,
  from,
  through,
}: {
  tenantId: string;
  from: string;
  through: string;
}) {
  const { data, loading } = useQuery(WASTE_SCHEDULE_ADHERENCE_SUMMARY_QUERY, {
    variables: { tenantId, from, through },
    skip: !tenantId || from === "",
  });

  const summary = data?.wasteScheduleAdherence.summary;
  if (loading && !summary) {
    return <InvoiceTrackingSummarySkeleton count={5} />;
  }
  if (!summary) return null;

  return (
    <InvoiceTrackingSummaryCard>
      <SummaryTiles summary={summary} />
    </InvoiceTrackingSummaryCard>
  );
}

/**
 * What the reader is looking at, and anything they need to know before reading
 * it. Sits with the list rather than with the totals, because it explains the
 * rows: the totals are on their own card above.
 *
 * Reads the same query as {@link PeriodTotals}; Apollo serves the second caller
 * from cache rather than asking twice.
 */
export function ReportIntro({
  tenantId,
  from,
  through,
}: {
  tenantId: string;
  from: string;
  through: string;
}) {
  const { data } = useQuery(WASTE_SCHEDULE_ADHERENCE_SUMMARY_QUERY, {
    variables: { tenantId, from, through },
    skip: !tenantId || from === "",
  });
  const summary = data?.wasteScheduleAdherence.summary;
  // The heading and what a row is do not depend on the figures, so they are
  // here from the first paint: waiting would shunt the list down the page when
  // the totals land.
  return (
    <>
      <InvoiceTrackingListHeading title="Schedule Adherence" />
      {summary != null && noInvoiceCoverage(summary) ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          No invoices have been imported for this period, so every scheduled window is unresolved
          for that one reason rather than for anything the schedule says. Import the period&apos;s
          invoices to grade it, or choose a period that has them.
        </Alert>
      ) : null}
    </>
  );
}
